import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { EmptyState, LoadingState } from '@/components/ui/states';
import { PROMOTION_TYPES, type Promotion, type PromotionType, apiError } from './lib';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const BLANK = {
  name: '',
  description: '',
  type: 'PERCENTAGE' as PromotionType,
  value: '10',
  minSpend: '',
  maxDiscount: '',
  daysOfWeek: [] as number[],
  startTime: '',
  endTime: '',
  requiresCoupon: false,
  priority: '0',
  isActive: true,
};

interface Props {
  promotions: Promotion[];
  loading: boolean;
  onChanged: () => void;
}

export default function PromotionsTab({ promotions, loading, onChanged }: Props) {
  const { t } = useTranslation();
  // A promotion's fixed value, minimum spend and discount cap are money in the
  // branch's currency; the PERCENTAGE variant is a plain number.
  const { code: currency, format: formatMoney, toMoney, decimals } = useCurrency();
  const moneyStep = decimals === 0 ? '1' : `0.${'0'.repeat(decimals - 1)}1`;
  const isAmount = (type: PromotionType) => type !== 'PERCENTAGE';
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const canCreate = can('menu.create');
  const canUpdate = can('menu.update');
  const canDelete = can('menu.delete');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setFormOpen(true);
  };

  const openEdit = (promotion: Promotion) => {
    setEditing(promotion);
    setForm({
      name: promotion.name,
      description: promotion.description ?? '',
      type: promotion.type,
      value: promotion.value,
      minSpend: promotion.minSpend ?? '',
      maxDiscount: promotion.maxDiscount ?? '',
      daysOfWeek: promotion.daysOfWeek ?? [],
      startTime: promotion.startTime ?? '',
      endTime: promotion.endTime ?? '',
      requiresCoupon: promotion.requiresCoupon,
      priority: String(promotion.priority),
      isActive: promotion.isActive,
    });
    setFormOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    const body = {
      name: form.name,
      description: form.description || undefined,
      type: form.type,
      value: isAmount(form.type) ? toMoney(form.value || '0') : form.value || '0',
      minSpend: form.minSpend ? toMoney(form.minSpend) : undefined,
      maxDiscount: form.maxDiscount ? toMoney(form.maxDiscount) : undefined,
      daysOfWeek: form.daysOfWeek,
      startTime: form.startTime || undefined,
      endTime: form.endTime || undefined,
      requiresCoupon: form.requiresCoupon,
      priority: Number(form.priority) || 0,
      isActive: form.isActive,
    };
    try {
      if (editing) await api.patch(`/promotions/${editing.id}`, body);
      else await api.post('/promotions', body);
      toast.success(t(editing ? 'crm.promotions.updated' : 'crm.promotions.created'));
      setFormOpen(false);
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('crm.promotions.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (promotion: Promotion) => {
    try {
      await api.post(`/promotions/${promotion.id}/deactivate`, {});
      toast.success(t('crm.promotions.deactivated'));
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('crm.promotions.saveFailed')));
    }
  };

  const remove = async (promotion: Promotion) => {
    if (!(await confirm({ title: t('crm.promotions.deleteConfirm', { name: promotion.name }), tone: 'danger' }))) return;
    try {
      await api.delete(`/promotions/${promotion.id}`);
      toast.success(t('crm.promotions.deleted'));
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('crm.promotions.saveFailed')));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-muted">{t('crm.promotions.hint')}</p>
        {canCreate && <Button onClick={openCreate}>{t('crm.promotions.newPromotion')}</Button>}
      </div>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : promotions.length === 0 ? (
        <EmptyState title={t('crm.promotions.empty')} hint={t('crm.promotions.emptyHint')} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {promotions.map((promotion) => (
            <Card key={promotion.id}>
              <CardHeader className="flex-row items-start justify-between gap-2">
                <div>
                  <CardTitle>{promotion.name}</CardTitle>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <Badge tone="info">{t(`crm.promotionType.${promotion.type}`)}</Badge>
                    <Badge tone="neutral">
                      {promotion.type === 'PERCENTAGE'
                        ? `${promotion.value}%`
                        : formatMoney(promotion.value)}
                    </Badge>
                    {promotion.requiresCoupon && (
                      <Badge tone="warning">{t('crm.promotions.needsCoupon')}</Badge>
                    )}
                    <Badge tone={promotion.isActive ? 'success' : 'neutral'}>
                      {t(promotion.isActive ? 'catalog.items.active' : 'catalog.items.inactive')}
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  {canUpdate && (
                    <Button size="sm" variant="secondary" onClick={() => openEdit(promotion)}>
                      {t('common.edit')}
                    </Button>
                  )}
                  {canUpdate && promotion.isActive && (
                    <Button size="sm" variant="outline" onClick={() => deactivate(promotion)}>
                      {t('crm.promotions.deactivate')}
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="sm" variant="danger" onClick={() => remove(promotion)}>
                      {t('common.delete')}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {promotion.description && <p>{promotion.description}</p>}
                <p className="text-xs text-ink-muted">
                  {promotion.daysOfWeek?.length
                    ? promotion.daysOfWeek.map((day) => t(`catalog.menus.day.${DAY_KEYS[day]}`)).join(', ')
                    : t('crm.promotions.everyDay')}
                  {promotion.startTime && promotion.endTime
                    ? ` · ${promotion.startTime}–${promotion.endTime}`
                    : ''}
                </p>
                {promotion.minSpend && (
                  <p className="text-xs text-ink-muted">
                    {t('crm.coupons.minSpend', {
                      amount: formatMoney(promotion.minSpend),
                    })}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t(editing ? 'crm.promotions.editPromotion' : 'crm.promotions.newPromotion')}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} disabled={saving || !form.name.trim()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('crm.promotions.name')} required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label={t('crm.promotions.description')}>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('crm.promotions.type')}>
              <Select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as PromotionType })}
              >
                {PROMOTION_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {t(`crm.promotionType.${value}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={t('crm.promotions.value')}
              hint={
                isAmount(form.type)
                  ? t('currency.amountIn', { code: currency })
                  : t('crm.promotions.valueHint')
              }
            >
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step={isAmount(form.type) ? moneyStep : '0.01'}
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
              />
            </Field>
            <Field
              label={t('crm.coupons.minSpendLabel')}
              hint={t('currency.amountIn', { code: currency })}
            >
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step={moneyStep}
                value={form.minSpend}
                onChange={(e) => setForm({ ...form, minSpend: e.target.value })}
              />
            </Field>
            <Field
              label={t('crm.promotions.maxDiscount')}
              hint={t('currency.amountIn', { code: currency })}
            >
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step={moneyStep}
                value={form.maxDiscount}
                onChange={(e) => setForm({ ...form, maxDiscount: e.target.value })}
              />
            </Field>
            <Field label={t('crm.promotions.startTime')}>
              <Input
                type="time"
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              />
            </Field>
            <Field label={t('crm.promotions.endTime')}>
              <Input
                type="time"
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              />
            </Field>
          </div>

          <Field label={t('crm.promotions.days')} hint={t('crm.promotions.daysHint')}>
            <div className="flex flex-wrap gap-2">
              {DAY_KEYS.map((key, day) => {
                const on = form.daysOfWeek.includes(day);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        daysOfWeek: on
                          ? form.daysOfWeek.filter((d) => d !== day)
                          : [...form.daysOfWeek, day].sort(),
                      })
                    }
                    className={`rounded-lg px-2.5 py-1 text-xs ${
                      on ? 'bg-accent-solid text-accent-on' : 'border text-ink-muted'
                    }`}
                  >
                    {t(`catalog.menus.day.${key}`)}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label={t('crm.promotions.priority')} hint={t('crm.promotions.priorityHint')}>
            <Input
              type="number"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            />
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.requiresCoupon}
              onChange={(e) => setForm({ ...form, requiresCoupon: e.target.checked })}
            />
            {t('crm.promotions.needsCoupon')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            {t('catalog.items.active')}
          </label>
        </div>
      </Dialog>
    </div>
  );
}
